/**
 * 装配体多零件报价(quote projects)API client · 第一阶段 M5 前端。
 *
 * 铁律与 `factorQuotes.ts` 一致:**所有金额由后端确定性引擎算**,前端只发输入 +
 * 展示后端返回的 DTO,绝不在客户端做任何成本/价格/合计的运算。
 * 汇总页上那些「合计」「套价」「综合利润率」全部取后端 `totals`,不在这里加减。
 *
 * 类型**从生成的 OpenAPI 契约推导**(`components["schemas"][…]`),不手抄字段 ——
 * 手抄的话后端改了字段前端不会红,要等运行时才炸(警示 #17 形态①:跨层字段失配)。
 */
import type { components, operations } from "@/api/generated/openapi";
import { apiClient } from "@/api/client";
import type { CustomMaterialSpec } from "@/api/factorQuotes";

/* ── 契约派生类型(不手抄) ───────────────────────────────── */

/** 清单行类别 —— 从 PATCH 接口的契约派生(后端唯一来源 `quote_project.PROJECT_LINE_KINDS`)。 */
export type ProjectLineKind = NonNullable<
  components["schemas"]["UpdateLineRequest"]["kind"]
>;

/**
 * 进计价的类别 —— 与后端 `quote_project.QUOTABLE_KINDS`、报价 skill 的
 * `PRICED_CATEGORIES = ("make", "sheet_metal")` 同集合(`test_quote_line_kinds` 钉住)。
 * 标准件 / 外购件只列清单(D1),需人工报价(如焊接件)列入「本单不含」。
 */
export const QUOTABLE_KINDS: ReadonlySet<ProjectLineKind> =
  new Set<ProjectLineKind>(["made", "sheet_metal"]);
export type ProjectStatus = "draft" | "pricing" | "ready" | "failed";

export type SummaryRequest = components["schemas"]["SummaryRequest"];

type SummaryOp =
  operations["summarize_quote_project_v1_quote_projects__project_id__summary_post"];
/** 汇总响应。后端返回的是自由形状的三层投影,这里按实际键补上可读类型。 */
export type SummaryRaw =
  SummaryOp["responses"][200]["content"]["application/json"];

/* ── 后端 DTO 的可读形状(字段名与 quote_projects.py 逐字对齐) ── */

export type ProjectReadiness =
  components["schemas"]["ProjectReadinessResponse"];
export type ProjectReview = components["schemas"]["ProjectReviewResponse"];
export type ProjectPrefillResult =
  components["schemas"]["ProjectPrefillResponse"];
export type UnitPriceSources = NonNullable<
  components["schemas"]["ProjectReadResponse"]["unit_price_sources"]
>;
export interface ProjectLine extends Partial<
  Pick<
    components["schemas"]["ProjectLineReadResponse"],
    | "draft_behind"
    | "draft_behind_reasons"
    | "quote_status"
    | "approval_effective"
    | "readiness"
    | "provisional"
    | "shop_fact_sources"
  >
> {
  id: string;
  part_index: number;
  name: string;
  kind: ProjectLineKind;
  kind_auto: ProjectLineKind;
  kind_overridden: boolean;
  class_reason: string;
  needs_review: boolean;
  qty_per_set: number;
  spare_qty: number;
  qty_override: number | null;
  /** 该行件数 = 每套用量 × 套数 + 备品(后端算,前端不乘) */
  quantity: number;
  included: boolean;
  quote_id: string | null;
  /** 关联的零件档(P4 · 方案 §5.8)· 零件档的编号 / 图号走 `getProjectPartLinks`;旧后端没有这一项 */
  part_id?: string | null;
  drawing_page: number | null;
  /**
   * 这一行自己的 3D / 图纸文档。零散多件、补传按文件名配上的是**整份就是这件的图**(没有页码);
   * 配了页码的,是这份 PDF 的第几页
   */
  cad_document_id?: string | null;
  drawing_document_id?: string | null;
  /**
   * 这一行有没有 3D 模型(旧后端没有这一项)。`false` = 尺寸、重量、工时只能读图估(精度差),
   * 工艺按读图判断预填、没有 3D 核对过(请核对);可以逐行补传 3D
   */
  has_3d?: boolean;
  /** 行自己挂的 3D 的文件名(装配体拆出来的行没有:它的 3D 在装配体里) */
  cad_file?: string | null;
  /** 能不能在这一行补传 / 换 3D:没有 3D 的行,和 3D 是补传上来的行 */
  cad_assignable?: boolean;
  /**
   * 补传了 3D、报价还没换上它:行上的价不是按这份 3D 算的。报价还是草稿的话,再点「开始报价」按 3D 重算;
   * 已经送审 / 批准的不会重算
   */
  cad_pending?: boolean;
  /**
   * 补传的 3D 里数到几个实体 —— **只在不是 1 个时有**:多于 1 = 人确认过,这一行的尺寸、重量、工时是这几个实体合计;
   * 0 = 转换成功却没数到实体(多半只有曲面),尺寸和工时可能读不出来。
   */
  cad_bodies?: number | null;
  /** 图纸所在 PDF 的文件名(补传过几份 PDF 后,只看页码分不清是哪份的第几页) */
  drawing_file?: string | null;
  /**
   * 图纸怎么配上的:精确 / 规范化 / 模糊(0.xx)/ 仅图纸 / 人工。
   * 「模糊」= 按近似名自动配上、人还没确认 —— 没确认前这一页不拿来报价
   */
  drawing_match?: string | null;
  /** 逐件推理(后端 part_inference):每项 = 值 + 来源 + 依据 + 是否确定。没有图纸时为 null */
  inference?: LineInference | null;
  /** 本行已保存的图纸字段快照;来源只指向用于本行推理的那一页。旧记录可能没有来源元信息。 */
  titleblock?: TitleblockFacts | null;
  /**
   * 草稿里每项实际取自哪里:drawing 图纸推理 / project 本项目参数 / tenant 工厂默认 /
   * system 系统默认 / user 人在工作表或批量改里定的。还没建草稿时为 null
   */
  applied?: Record<
    string,
    "drawing" | "project" | "tenant" | "system" | "user"
  > | null;
  /**
   * 读图填进草稿的工艺还要不要人核对、为什么(后端 `part_inference.process_review` 判,前端不另判):
   * null / 缺省 = 不用(没填、3D 核对通过、或后来有人 / 项目参数 / 工厂默认另定过;旧后端不给这一项)。
   */
  process_review?: ProcessReview | null;
  /**
   * 有人对读图填的工艺(现在这个样子)点过「核对无误」:谁、什么时候。之后 3D 结论变了就回到 null,
   * 这一行重新要核对(`process_review` 又有值)。旧后端不给这一项。
   */
  process_reviewed?: { by: string | null; at: string } | null;
  /** 这一行草稿预填到哪了:pending 排队 / running 在算 / ready / failed;没草稿(或旧后端)= null / 不给 */
  prefill_state?: "pending" | "running" | "ready" | "failed" | null;
  /** 当前失败的原始原因，仅failed时有值；旧后端可能没有，默认不直接展示。 */
  prefill_error?: components["schemas"]["ProjectLineReadResponse"]["prefill_error"];
  /**
   * 失败的机器原因码(阶段三 S3-5):`cancelled` = 人取消了,不算识别失败、「重试失败的 N 行」不带它,
   * 要逐行「继续算」;其余照失败显示。旧后端不给。
   */
  prefill_reason?: components["schemas"]["ProjectLineReadResponse"]["prefill_reason"];
  /** 这一行还在排队时,本厂同一条道上比它早、还在排队的张数(阶段三 S3-6 · M5.3);只有详情带 */
  queue_ahead?: components["schemas"]["ProjectLineReadResponse"]["queue_ahead"];
  /**
   * 预填完了还缺的必填输入(给人看的叫法,如「材料单价」「材料密度」)· 非空 = 这一行算不出完整的价。
   * 还在算 / 没草稿 = 空;旧后端不给。
   */
  missing_inputs?: string[];
}

export type TitleblockField =
  | "name"
  | "drawing_no"
  | "material_raw"
  | "surface"
  | "heat_treat"
  | "weight_raw"
  | "scale";

/** 来源树包含几何栏位、文字回退及编号技术要求;未解决的栏位可能只保存理由与位置。 */
export interface TitleblockSource {
  method?:
    | "right"
    | "below"
    | "inline"
    | "empty"
    | "text"
    | "technical_requirement"
    | "conflict"
    | "unresolved"
    | "unrecognized";
  state?: "value" | "empty" | "unknown" | "conflict";
  direction?: "right" | "below" | "inline";
  label?: string;
  bbox?: [number, number, number, number];
  reason?: string;
  evidence?: TitleblockSource[];
  unresolved_sources?: TitleblockSource[];
  value?: string | number | null;
  prohibited?: boolean;
  requirement?: number;
  line?: number;
}

/** 读取规则归报价 skill 所有;前端只展示已有事实和来源,不参与识别或计价。 */
export interface TitleblockFacts {
  name?: string | null;
  drawing_no?: string | null;
  material_raw?: string | null;
  material?: string | null;
  surface?: string | null;
  heat_treat?: string | null;
  weight_raw?: number | null;
  weight_unit?: string | null;
  scale?: string | null;
  tech_requirements?: string[];
  special_notes?: string[];
  general_tolerance?: Record<string, unknown> | null;
  reader?: "text" | "geometry";
  page?: number;
  needs_review?: boolean;
  field_sources?: Partial<Record<TitleblockField, TitleblockSource>>;
}

/**
 * 读图填的工艺为什么还要人核对:blocked 3D 显示可能要多轴(很可能报低了)· no_3d 只有图纸没法核对 ·
 * no_result 有 3D 却没核对出结果 · multi_axis 读图判的是多轴(分不准是哪一种)·
 * process_class 读图只分得出车削 / 钣金这一大类,填的是这一类最常用的那道 · unchecked 别的没核对过的(旧记录)
 */
export type ProcessReview =
  | "blocked"
  | "no_3d"
  | "no_result"
  | "multi_axis"
  | "process_class"
  | "unchecked";

/** 推理里的一个目录条目 */
export interface InferredPick {
  id: string;
  code: string;
  name_zh: string;
  grade: string | null;
}

/** 一个推理字段。`sure=false` = 并列 / 只是相近 / 与重量对不上 —— 界面标「需确认」,不当结论用 */
export interface InferredField<T = InferredPick | null> {
  value: T;
  source: string;
  sure: boolean;
  why: string;
  candidates?: InferredPick[];
}

/** 表处理的一道 */
export interface InferredPass {
  text: string;
  picked: InferredPick[];
  sure: boolean;
  why: string;
  candidates: InferredPick[];
}

/**
 * 读图判断的主工艺(计划 §5.12)。**一律 `sure=false`**:40 件实测判为三轴的件里有错的,
 * 错的是五轴贵件。`adopted` = 已按读图填进草稿 —— 2026-10-14 起读图识别到的铣削工艺(三轴 / 四轴 / 3+2 / 五轴联动)
 * 都先填、人来核对(2026-10-21 起车削 / 钣金也按大类填数控车削 / 激光切割);没填的是目录里没有那道工艺,
 * 和这之前预填的旧记录。
 */
export interface InferredProcess {
  value: { code: string; name_zh: string } | null;
  source: string;
  sure: boolean;
  adopted: boolean;
  /**
   * 读图判为三轴时的 3D 交叉核对结论(计划 §5.18;没判三轴为 null / 缺省):
   * pass 核对通过 · no_3d 只有图纸没法核对 · blocked 3D 显示可能要多轴 · no_result 有 3D 却没核对出结果。
   * 结论不决定填不填(2026-10-14 起都按读图填入),决定标签上怎么提醒、清单把哪些行点出来请人核对。
   */
  check?: "pass" | "no_3d" | "blocked" | "no_result" | null;
  why: string;
}

export interface LineInference {
  material?: InferredField;
  process?: InferredProcess;
  surfaces?: { passes: InferredPass[]; notes: string[]; source: string };
  heat_treat?: InferredField;
  tolerance?: InferredField<string>;
  weight?: InferredField<number | null> & { raw: number; unit: string | null };
}

export interface ProjectChecklistSummary {
  total_lines: number;
  made: number;
  standard: number;
  purchased: number;
  unknown: number;
  /** 钣金件(图纸注明)· 与自制件一样计价 */
  sheet_metal: number;
  /** 需人工报价(如焊接件)· 不计价 */
  unsupported: number;
  /** 计价的行数(`QUOTABLE_KINDS`:自制件 + 钣金件)—— 「开始报价」按这个数 */
  quotable_lines: number;
  needs_review: number;
  quotable_pieces: number;
}

/** 单价表(§8.2 第 2 层):`{目录项 id: {价格字段: 值}}`,同一目录项的所有行共用。 */
export type UnitPriceTable = Record<string, Record<string, number>>;

/** 零散多件按名称配对的结果(计划 §5.17)—— 建项目后先给人看 */
export interface LoosePairing {
  paired: number;
  /** 只有 3D、没配到图纸的零件名 */
  cad_only: string[];
  /** 只有图纸、没有 3D 的零件名(已按「只有图」建行) */
  drawing_only: string[];
  /** 同名重复、没法按名称配的名字(规范化后) */
  duplicates: string[];
  /** 合并 PDF 里认不出零件名的页数(总装图、明细表)· 没建行 */
  unnamed_pages: number;
}

export interface QuoteProjectDto extends Partial<
  Pick<
    components["schemas"]["ProjectReadResponse"],
    | "readiness"
    | "review"
    | "outcome"
    | "unit_price_sources"
    | "watermark_from"
    | "missing_unit_prices"
    /** 谁建的、谁送审的(只读事实):「撤回整单」给这两个人和审批角色看(后端锁前锁后另判) */
    | "created_by"
    | "submitted_by"
    /** 第几版、基于哪个、被哪个取代(阶段三 S3-3 · 项目出新版本;老项目读作 V1) */
    | "version"
    /** 排队情况(阶段三 S3-6 · M5.3):还在排的行数、在识别的行数、排得最前的那一行前面还有几张;只有详情带 */
    | "queue"
    | "event_seq"
  >
> {
  id: string;
  cad_document_id: string | null;
  drawing_document_id: string | null;
  name: string;
  /**
   * "loose" = 零散多件(各行挂各自的 3D / 图纸);"multi_body" = 多实体文件按实体拆出
   * (行名是「实体 k」);装配体 / 只有图纸的项目为 null
   */
  source?: string | null;
  pairing?: LoosePairing | null;
  status: ProjectStatus;
  sets: number;
  summary: ProjectChecklistSummary;
  /** 第 1 层 · 项目参数(整单默认,可逐行覆盖)· 只含**本项目明确选过**的键 */
  params: Record<string, unknown>;
  /** 实际生效的项目参数 + 来源(本项目 / 工厂默认 / 系统默认)—— 开工前给人确认 */
  effective_params?: Record<string, EffectiveParam>;
  /** 第 2 层 · 单价表(改一处全部生效) */
  unit_prices: UnitPriceTable;
  /** 订单级费用(整单只收一次,不摊进行单价)· 汇总与导出不另传时都用它 */
  order_fees?: ProjectOrderFees;
  lines: ProjectLine[];
}

/** 订单级费用:各项金额(元)+ 加急系数(按零件合计,0.2 = +20%)。 */
export interface ProjectOrderFees {
  fees: Record<string, number>;
  rush_pct: number;
  /** 界面给输入的常用项(运输、包装、出货检验、试装配 / 组装、其他) */
  items: string[];
}

/** 汇总块(后端 `summary_block`)· 键是中文,与报价单用语一致。 */
export interface ProjectTotalsBlock {
  件数: number;
  自制件行数: number;
  零件合计: number;
  纯加工合计: number;
  订单级费用: Record<string, number>;
  订单级合计: number;
  总价: number;
  套价: number | null;
  /** 单列说明,**已摊在各行单价里**,不重复计入合计 */
  其中一次性工程费: number;
  /** 按成本加权,不是各行利润率的算术平均 */
  综合利润率: number;
  /** 要人确认的提醒(小批量表处理 / 热处理没钉起步价 …)· 旧版接口没有 */
  提醒?: string[];
}

export interface NotPricedLine {
  line_id: string;
  name: string;
  reason: string;
}

/** 标定域标记(报价 skill 判定):extrapolated = 域外外推;thin = 域内但样本少 */
export interface DomainFlag {
  kind: string;
  level: "extrapolated" | "thin";
  text: string;
}

/**
 * 哪些价有本厂真实价支撑(计划 M6 第 6 条)。`lines` 只列有标记的行,只放和零件本身有关的原因;
 * `notes` 是整单一样的说明(本厂未标定、数量超出标定、按平台口径计价的行数)。
 */
export interface ProjectCalibration {
  lines: { line_id: string; name: string; flags: DomainFlag[] }[];
  notes: string[];
}

/** 一件已确认价的零件(报价 skill 类推比较法找出来的)· `unit_price` 的键是数量 */
export interface SimilarCase {
  id: string;
  name: string;
  similarity: "高" | "中" | "低";
  distance: number;
  scope: string;
  material: string;
  process: string;
  unit_price: Record<string, number>;
  why: string;
}

/**
 * 本厂丢过单的相似件(价格上沿参照)· `quoted_price` = 当时报出的单价(键是数量)。
 * 丢单价只说明「客户能接受的比它低」,不是这件的真实价。
 */
export interface LostCase {
  id: string;
  name: string;
  similarity: "高" | "中" | "低";
  distance: number;
  scope: string;
  material: string;
  process: string;
  quoted_price: Record<string, number>;
  why: string;
}

/**
 * 这个价离不离谱(计划 M6 第 5 条):每行最相似的已确认件 + 本行按同样数量的单价 + 差多少。
 * `diff` 只在相似度高 / 中时给;`scope` 说明参照来自本厂成交还是默认底板;`unmatched` = 找不了的行数。
 */
export interface ProjectSimilar {
  scope: string;
  lines: {
    line_id: string;
    name: string;
    /** 只有丢单参照(本厂还没有成交件)的行为 null,`cases` 为空 */
    qty: number | null;
    unit_price: number | null;
    diff: number | null;
    cases: SimilarCase[];
    /** 最相似的 2 件本厂丢单(旧版接口没有)· `warnings` = 本次不低于高相似丢单价时的提示 */
    lost?: LostCase[];
    warnings?: string[];
  }[];
  unmatched: number;
  /** 本厂还没有成交案例(不再退回样例 40 件)· `reason` 照说 */
  no_own_cases?: boolean;
  reason?: string | null;
}

export interface ProjectSummary extends Partial<
  Pick<
    components["schemas"]["ProjectSummaryReadResponse"],
    "readiness" | "review"
  >
> {
  /** 暂估行的后端投影；只显示，不采纳或推算价格。 */
  lines?: {
    line_id: string;
    provisional?: components["schemas"]["ProvisionalPriceResponse"] | null;
    computed_unit_price?: number | null;
    [key: string]: unknown;
  }[];
  project_id: string;
  sets: number;
  /** 套数档位(不是件数) */
  tiers: number[];
  /** true = 有行没算进去 —— 总价不完整,界面**必须**说出来 */
  partial: boolean;
  not_priced: NotPricedLine[];
  /** 第 1 层 · 报价单 */
  quote_sheet: Record<string, string | number | boolean>[];
  /** 第 2 层 · 计算明细(缺值是 null,不是 0) */
  detail_sheet: Record<string, string | number | null>[];
  /** 第 3 层 · 参数与来源 */
  params: {
    参数: string;
    取值: number | string | null;
    单位: string;
    来源: string;
    说明: string;
    用到的零件数: number;
  }[];
  /** 键是套数的字符串形式 */
  totals: Record<string, ProjectTotalsBlock>;
  /** 标定域(旧版接口没有这一项) */
  calibration?: ProjectCalibration;
  /** 相似件参照;查参照出错时为 null(旧版接口没有这一项) */
  similar?: ProjectSimilar | null;
  /** 成本结构拆分(对齐方案 §6.3 · 内部 · 键是套数的字符串形式;旧版接口没有这一项) */
  split?: Record<string, ProjectSplitBlock>;
}

/** 装配体一档的成本结构:每行每件的拆分 + 整单逐项合计;订单级费用单列 → 合计 = 该档总价 */
export interface ProjectSplitBlock {
  lines: (import("./factorQuotes").CostSplitView & {
    name: string;
    qty: number;
  })[];
  parts: { key: string; label: string; amount: number }[];
  parts_total: number;
  order_fees: { label: string; amount: number }[];
  total: number;
  /** 没有量产方案的行(只有加工费,没有纯加工费用) */
  rows_without_plan: string[];
}

/* ── 调用 ────────────────────────────────────────────────── */

/**
 * 建项目的结果。
 *
 * ⚠ **大装配走后台拆解时,后端返 202 且响应里没有 `id`**
 * (63MB / 433 实例的装配体必然走这条)。类型做成可辨识联合,**逼调用方处理** ——
 * 直接当 `QuoteProjectDto` 用会拿到 `undefined` 当项目 id,
 * 跳到 `/quote-projects/undefined`(2026-09-26 用户实测踩到)。
 */
export type CreateProjectResult =
  | { status: "ready"; project: QuoteProjectDto }
  | { status: "decomposing"; detail: string };

export async function createQuoteProject(body: {
  cad_document_id?: string | null;
  drawing_document_id?: string | null;
  sets?: number;
  name?: string | null;
  /** 零件文件里有多个实体,人选了「按实体拆开报价」:每个实体一行 */
  split_bodies?: boolean;
  /** 建单请求号(阶段三 S3-2 · `@/api/requestIds`):重试 / 双击带同一个号,后端只建一个项目 */
  request_id?: string | null;
}): Promise<CreateProjectResult> {
  const res = await apiClient.post<
    QuoteProjectDto | { status: string; detail?: string }
  >("/v1/quote-projects", body);
  const data = res.data as Record<string, unknown>;
  if (res.status === 202 || data?.status === "decomposing" || !data?.id) {
    return {
      status: "decomposing",
      detail: String(data?.detail ?? "大装配后台拆解中,请稍候"),
    };
  }
  return { status: "ready", project: data as unknown as QuoteProjectDto };
}

/**
 * 轮询建项目直到拆好。
 *
 * 后台拆解在途时**响应里没有项目 id**,所以没法「GET 轮询」——
 * 只能拿同一个 `cad_document_id` **重复 POST**(后端幂等:同一文档返既有项目)。
 * 带了 `request_id` 的,每次轮询都带同一个号(拆解在途时后端什么都没建,号还没记下)。
 */
export async function createQuoteProjectPolling(
  body: Parameters<typeof createQuoteProject>[0],
  opts: {
    intervalMs?: number;
    timeoutMs?: number;
    onWait?: (detail: string) => void;
  } = {},
): Promise<QuoteProjectDto> {
  const interval = opts.intervalMs ?? 3000;
  const timeout = opts.timeoutMs ?? 5 * 60_000;
  const deadline = Date.now() + timeout;
  for (;;) {
    const r = await createQuoteProject(body);
    if (r.status === "ready") return r.project;
    opts.onWait?.(r.detail);
    if (Date.now() + interval > deadline) {
      // 说清是「还没拆完」不是「失败了」—— 用户再点一次即可,不用重传文件
      throw new Error(
        `装配体还在拆解中(已等 ${Math.round(timeout / 1000)} 秒)· 稍后再点一次即可,不用重新上传`,
      );
    }
    await new Promise((res) => setTimeout(res, interval));
  }
}

/** 报价项目从哪来的:装配体 / 只有图纸(合并 PDF)/ 零散多件 / 多实体拆开 */
export type QuoteProjectKind = "assembly" | "drawings" | "loose" | "multi_body";

/** 项目列表里的一行(`GET /v1/quote-projects` · 最新建的在前)。 */
export interface QuoteProjectListItem {
  id: string;
  name: string;
  status: string;
  sets: number;
  /** 第几版(阶段三 S3-3):2 起才有;老后端 / V1 没有这一项 */
  version_no?: number | null;
  kind: QuoteProjectKind;
  created_at: string | null;
  /** 清单一共几行 */
  lines: number;
  /** 要报价的几行(勾选了的自制件 + 钣金件) */
  lines_to_price: number;
}

export async function listQuoteProjects(): Promise<QuoteProjectListItem[]> {
  const { data } = await apiClient.get<{ projects: QuoteProjectListItem[] }>(
    "/v1/quote-projects",
    { params: { limit: 100 } },
  );
  return data.projects;
}

export async function getQuoteProject(
  projectId: string,
): Promise<QuoteProjectDto> {
  const { data } = await apiClient.get<QuoteProjectDto>(
    `/v1/quote-projects/${projectId}`,
  );
  return data;
}

export async function updateQuoteProject(
  projectId: string,
  body: components["schemas"]["UpdateProjectRequest"],
): Promise<QuoteProjectDto> {
  const { data } = await apiClient.patch<QuoteProjectDto>(
    `/v1/quote-projects/${projectId}`,
    body,
    "exclusions" in body ? { _silentError: true } : undefined,
  );
  return data;
}

export async function updateProjectLine(
  projectId: string,
  lineId: string,
  body: components["schemas"]["UpdateLineRequest"],
  options?: { silent?: boolean },
): Promise<ProjectLine> {
  // 后端返回的是**改后的那一行**,不是整个项目(调用方要整个项目就重拉)
  const { data } = await apiClient.patch<ProjectLine>(
    `/v1/quote-projects/${projectId}/lines/${lineId}`,
    body,
    options?.silent || "provisional" in body
      ? { _silentError: true }
      : undefined,
  );
  return data;
}

/** 合并 PDF 逐页配对的结果。`matched` 是**配上零件的页数**,不是零件数。 */
export interface AttachDrawingsResult {
  total_pages: number;
  matched: number;
  ambiguous: number;
  unmatched: number;
  kept_manual: number;
  /** false = 这份 PDF 读不到文字层(扫描件)。**不是失败** —— 后端会走 VLM 兜底。 */
  text_layer: boolean;
  vlm_used: boolean;
  /** 图纸里有、3D 清单里没有 → 按零件名另建的行数 */
  created_lines: number;
  /** skill = 报价 skill 的标题栏解析;fallback = 平台原读法(扫描件 / 非国标模板 / 缺 poppler) */
  reader: "skill" | "fallback";
  pages: {
    page: number;
    part_index: number | null;
    candidates: number[];
    how: string;
  }[];
}

/**
 * 合并 PDF 图纸 → 按标题栏名字逐页配对 → 页码写进清单行。
 *
 * **可重复调**:人工配过的行不会被覆盖(后端按 `meta_json.drawing_source` 判)。
 * 扫描件要走 VLM 逐页转录,可能要几十秒,调用方自己给进度。
 */
export async function attachProjectDrawings(
  projectId: string,
  drawingDocumentId: string,
): Promise<AttachDrawingsResult> {
  const { data } = await apiClient.post<AttachDrawingsResult>(
    `/v1/quote-projects/${projectId}/drawings`,
    { drawing_document_id: drawingDocumentId },
  );
  return data;
}

/** 补传图纸:一份 PDF 配到了哪几行(计划 §5.17 补完)。 */
export interface DrawingFileOutcome {
  document_id: string;
  filename: string;
  /** 文件名 = 按 PDF 文件名配上(整份);标题栏 = 合并图纸按标题栏逐页配;同名重复 / 没配上 = 要人指定 */
  how: "文件名" | "标题栏" | "同名重复" | "没配上";
  lines: string[];
  note: string;
  /** 以下只对按标题栏配页的(合并图纸)有意义 */
  total_pages: number;
  matched_pages: number;
  created_lines: number;
  unnamed_pages: number;
  ambiguous: number;
  vlm_used: boolean;
}

export interface AddDrawingFilesResult {
  files: DrawingFileOutcome[];
  /** 这次换上新图纸的行数(不含另建的行) */
  paired_lines: number;
  /** 其中原来就有图纸、被换掉的 */
  replaced: number;
  /** 名字对上了、但人工配过图纸所以没换的 */
  kept_manual: number;
  created_lines: number;
  ambiguous: number;
}

/**
 * 建好项目后补传几份图纸 PDF:先按文件名配到同名的行(整份),其余按标题栏逐页配。
 * 人工配过的行不覆盖;没配上的用 `assignLineDrawing` 手动指给某一行。
 */
export async function addProjectDrawingFiles(
  projectId: string,
  drawingDocumentIds: string[],
): Promise<AddDrawingFilesResult> {
  const { data } = await apiClient.post<AddDrawingFilesResult>(
    `/v1/quote-projects/${projectId}/drawings/files`,
    { drawing_document_ids: drawingDocumentIds },
  );
  return data;
}

/** 人把一份 PDF(整份,或其中第几页)指给某一行 · 记为人工配对。返回改后的那一行。 */
export async function assignLineDrawing(
  projectId: string,
  lineId: string,
  body: { drawing_document_id: string; page?: number | null },
): Promise<ProjectLine> {
  const { data } = await apiClient.put<ProjectLine>(
    `/v1/quote-projects/${projectId}/lines/${lineId}/drawing`,
    body,
  );
  return data;
}

/**
 * 给一行补传(或换)单个零件的 3D —— 只有图纸的行没有 3D。返回改后的那一行。
 * 已建草稿的行返回 `cad_pending=true`:再点「开始报价」才按 3D 重读几何(人改过的值不动)。
 */
export async function assignLineCad(
  projectId: string,
  lineId: string,
  cadDocumentId: string,
  opts: {
    /** 文件里不止一个实体、人确认过「确实是一个零件」才传 true(不传后端回 `LINE_CAD_MULTI_BODY`) */
    allowMultiBody?: boolean;
  } = {},
): Promise<ProjectLine> {
  const { data } = await apiClient.put<ProjectLine>(
    `/v1/quote-projects/${projectId}/lines/${lineId}/cad`,
    {
      cad_document_id: cadDocumentId,
      ...(opts.allowMultiBody ? { allow_multi_body: true } : {}),
    },
    { _silentError: true },
  );
  return data;
}

/** 后端说「这份 3D 里不止一个实体,要人确认」的错误代码(见 `assignLineCad`)。 */
export const LINE_CAD_MULTI_BODY = "LINE_CAD_MULTI_BODY";

/** 生效参数的来源:project 本项目选的 / tenant 工厂默认 / system 系统默认 */
export type ParamSource = "project" | "tenant" | "system";

export interface EffectiveParam {
  value: unknown;
  source: ParamSource;
}

/**
 * 工厂(租户)级报价默认(计划 §3 第 5 条 · 方案 B)。
 * `profit_ratio` 是**加价率**(总价 = 成本 × (1 + 利润)),不是费率卡里的毛利率。
 */
export interface QuoteDefaults {
  profit_ratio?: number;
  processing_mode?: "labor_only" | "turnkey";
  workstation_config_id?: string;
}

export interface QuoteDefaultsResponse {
  /** 本工厂设过的键 */
  defaults: QuoteDefaults;
  /** 没设的键用的系统默认 */
  system: { profit_ratio: number; processing_mode: string; tolerance: string };
}

/** 项目的商务条款块(项目上填一次 → 客户档案 → 工厂政策;与单件报价同一形状,项目没有图号 · 不冻结) */
export async function getProjectCommercial(
  projectId: string,
): Promise<import("./factorQuotes").CommercialView> {
  const { data } = await apiClient.get<import("./factorQuotes").CommercialView>(
    `/v1/quote-projects/${projectId}/commercial`,
  );
  return data;
}

/** 改项目的商务条款(出现的字段才改;null = 清掉回到默认)· 不改任何一行的价 */
export async function patchProjectCommercial(
  projectId: string,
  patch: Omit<import("./factorQuotes").CommercialPatch, "drawing_no">,
): Promise<import("./factorQuotes").CommercialView> {
  const { data } = await apiClient.patch<
    import("./factorQuotes").CommercialView
  >(`/v1/quote-projects/${projectId}/commercial`, patch, {
    _silentError: true,
    _errorContext: "保存项目商务条款失败",
  });
  return data;
}

export async function getQuoteDefaults(): Promise<QuoteDefaultsResponse> {
  const { data } =
    await apiClient.get<QuoteDefaultsResponse>("/v1/quote-defaults");
  return data;
}

/** 整体替换本工厂的报价默认(没给的键 = 不设,回到系统默认)。 */
export async function putQuoteDefaults(
  body: QuoteDefaults,
): Promise<QuoteDefaultsResponse> {
  const { data } = await apiClient.put<QuoteDefaultsResponse>(
    "/v1/quote-defaults",
    body,
  );
  return data;
}

/**
 * 「开始报价」请求体(契约派生)。`new_line_ids` =「先报前 a 行」:这次只给这些行新建草稿,
 * 其余新行这次不建、不扣额度、勾选不动(后端在同厂事务锁里校验)。不给 = 所有勾选的新行。
 */
export type PrefillProjectParams =
  components["schemas"]["PrefillProjectRequest"];

/** 建草稿额度不足的 402 detail(契约派生):原 code/message + 还剩几张、要几张、账期。 */
export type QuotaExceededDetail = components["schemas"]["QuotaExceededDetail"];

export async function prefillQuoteProject(
  projectId: string,
  params: PrefillProjectParams = {},
): Promise<ProjectPrefillResult> {
  // 失败一律由项目页自己显示(错误卡 / 额度卡),不再弹一遍全局提示
  const { data } = await apiClient.post(
    `/v1/quote-projects/${projectId}/prefill`,
    params,
    { _silentError: true },
  );
  return data;
}

/**
 * 整单汇总。**不落库、不改行**,每次按当下草稿现算,重复调是安全的。
 *
 * `tiers` 是**套数**档位(不是件数)—— 每行件数由后端按「每套用量 × 套数 + 备品」算。
 */
export async function summarizeQuoteProject(
  projectId: string,
  body: { tiers?: number[]; order_fees?: Record<string, number> } = {},
): Promise<ProjectSummary> {
  // 不传订单级费用 → 后端用项目里存的(PATCH /order-fees);传了只对这一次生效
  const { data } = await apiClient.post<ProjectSummary>(
    `/v1/quote-projects/${projectId}/summary`,
    {
      tiers: body.tiers ?? [1],
      ...(body.order_fees ? { order_fees: body.order_fees } : {}),
    },
  );
  return data;
}

/** 存订单级费用(整表替换 fees;金额 0 = 不收这一项)。不改零件行、不重算草稿。 */
export async function updateProjectOrderFees(
  projectId: string,
  body: { fees?: Record<string, number>; rush_pct?: number },
): Promise<ProjectOrderFees> {
  const { data } = await apiClient.patch<ProjectOrderFees>(
    `/v1/quote-projects/${projectId}/order-fees`,
    body,
  );
  return data;
}

export type ProjectQuotationVariant = "internal" | "customer";

/**
 * 导出项目报价单 · 与汇总同一份数据。
 *
 * - 内部版(默认):报价单 / 计算明细 / 参数与来源三个 sheet,带利润率、一次性工程费等内部信息
 * - 客户版:只有报价单页,内部信息由**服务端**删掉;`withPure` 时附纯加工合计。
 *   有未计价的行时后端拒绝(409),不完整的总价不发给客户;各行机时费没经本厂确认时
 *   也拒绝(409 `MACHINE_RATE_NOT_CONFIRMED`,对齐方案 F6)
 */
export async function downloadProjectQuotation(
  projectId: string,
  tiers: number[],
  opts: {
    variant?: ProjectQuotationVariant;
    withPure?: boolean;
    current?: () => boolean;
  } = {},
): Promise<void> {
  if (opts.current && !opts.current()) return;
  const variant = opts.variant ?? "internal";
  const { data } = await apiClient.post<Blob>(
    `/v1/quote-projects/${projectId}/quotation.xlsx`,
    { tiers },
    {
      params: { variant, with_pure: Boolean(opts.withPure) },
      responseType: "blob",
      ...(opts.current ? { _silentError: true } : {}),
      _errorContext: "报价单导出失败",
    },
  );
  if (opts.current && !opts.current()) return;
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `quote-project-${projectId.slice(0, 8)}${
    variant === "customer" ? "-customer" : ""
  }.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** 整单一键「采用默认值并钉为本厂」的结果:做了的行 / 做不了的行(说清原因)。 */
export interface AdoptProjectMachineRatesResult {
  adopted: {
    line_id: string;
    name: string;
    rates: import("@/utils/machineRateGate").UnconfirmedMachineRate[];
  }[];
  skipped: { line_id: string; name: string; reason: string }[];
}

/**
 * 整单一键「采用默认值并钉为本厂」(对齐方案 F6 · 出客户版前确认机时费):
 * 各行用的默认底板机时费 → 本厂钉价 + 标成本厂确认过 · 价不变。错误由调用方显示。
 */
export async function adoptProjectDefaultMachineRates(
  projectId: string,
): Promise<AdoptProjectMachineRatesResult> {
  const { data } = await apiClient.post<AdoptProjectMachineRatesResult>(
    `/v1/quote-projects/${projectId}/machine-rates/adopt-default`,
    undefined,
    { _silentError: true, _errorContext: "采用默认机时费失败" },
  );
  return data;
}

/**
 * 改单价表 → **同一目录项的所有行一起重定价**(方案 §8.2:改一处全部生效)。
 *
 * 便宜:纯改价不动几何,重预填会复用缓存几何,只重算单价。
 * `skipped` 里是**跟不上的行**(正在预填 / 已进人审流)—— 界面必须显示出来,
 * 不然人会以为全改了,而那几行还挂着旧价。
 */
export async function updateProjectUnitPrices(
  projectId: string,
  prices: UnitPriceTable,
  opts: { repricing?: boolean } = {},
): Promise<{
  unit_prices: UnitPriceTable;
  unit_price_sources?: UnitPriceSources;
  repriced: number;
  skipped: { name: string; reason: string }[];
}> {
  const { data } = await apiClient.patch(
    `/v1/quote-projects/${projectId}/unit-prices`,
    { prices, repricing: opts.repricing ?? true },
  );
  return data;
}

/** 改了**不用重跑几何**的项目参数(秒级)。与后端 `INSTANT_PARAMS` 逐字对齐。 */
export const INSTANT_PARAMS = ["profit_ratio", "processing_mode"] as const;
/** 改了**必须重跑几何**的(每行 STEP/PDF 10–40s)。与后端 `GEOMETRY_PARAMS` 对齐。 */
export const GEOMETRY_PARAMS = [
  "tolerance",
  "material_id",
  "custom_material",
  "process_id",
] as const;

export interface ProjectParamsPatch {
  profit_ratio?: number;
  processing_mode?: "labor_only" | "turnkey";
  tolerance?: "coarse" | "standard" | "precision" | "ultra";
  material_id?: string;
  /** 目录里没有的材料:人自己填(和 material_id 二选一) */
  custom_material?: CustomMaterialSpec;
  process_id?: string;
  /** 一次性费用怎么收(方案 D8):拆开收 / 按摊薄阈值 · 秒级,不重跑几何 */
  onetime_mode?: "per_order" | "threshold";
  /** 每批件数(拆开收时装夹调机按批次)· 秒级,不重跑几何 */
  lot_size?: number;
}

/**
 * 改项目参数 → **所有自制件行跟着改**(§8.2 第 1 层)。
 *
 * ⚠ 改 `tolerance` / `material_id` / `process_id` 会让每一行**重跑几何**
 * (10–40s/行)。界面必须在点之前就说清这件事 —— 响应里的 `needs_geometry`
 * 和 `reprefilled` 是给「正在重算 N 行,要几分钟」这类提示用的。
 */
export async function updateProjectParams(
  projectId: string,
  patch: ProjectParamsPatch,
): Promise<{
  params: Record<string, unknown>;
  changed: number;
  reprefilled: number;
  needs_geometry: boolean;
  skipped: { name: string; reason: string }[];
}> {
  const { data } = await apiClient.patch(
    `/v1/quote-projects/${projectId}/params`,
    patch,
  );
  return data;
}

export type BatchReselectRequest =
  components["schemas"]["BatchReselectRequest"];

/**
 * 勾选的多行一次改主工艺 / 材料(计划 M4 第 2 条)· 这些草稿在后端并发闸门内重预填、保留人改。
 * 跳过的行(已进人审、正在预填、还没建草稿)逐行给原因。
 */
export async function reselectProjectLines(
  projectId: string,
  body: BatchReselectRequest,
): Promise<{
  updated: number;
  skipped: { line_id: string; name: string; reason: string }[];
}> {
  const { data } = await apiClient.post(
    `/v1/quote-projects/${projectId}/lines/reselect`,
    body,
  );
  return data;
}

/**
 * 勾选的行:读图填的工艺「核对无误」—— **不改工艺、不重算价**,只是这些行不再提醒核对。
 * 现在不用核对的行(不是按读图填的、已经核对过 …)跳过并写明原因。行正被别人写着 → 409 `QUOTE_BUSY`。
 */
export async function markProcessReviewed(
  projectId: string,
  lineIds: string[],
): Promise<{
  reviewed: number;
  skipped: { line_id: string; name: string; reason: string }[];
}> {
  const { data } = await apiClient.post(
    `/v1/quote-projects/${projectId}/lines/process-reviewed`,
    { line_ids: lineIds },
  );
  return data;
}

export type CreateLooseProjectRequest =
  components["schemas"]["CreateLooseProjectRequest"];

/**
 * 零散多件建项目(计划 §5.17):每个 3D 一行,图纸**按名称**配上去 ——
 * PDF 文件名(或合并 PDF 里每页标题栏的「名称」)要和 3D 文件名一致。
 * 返回的项目里 `pairing` 是配对结果,先给人看。
 */
export async function createLooseQuoteProject(
  body: CreateLooseProjectRequest,
): Promise<QuoteProjectDto> {
  const { data } = await apiClient.post<QuoteProjectDto>(
    "/v1/quote-projects/loose",
    body,
  );
  return data;
}

export type ProjectApproveRequest =
  components["schemas"]["ProjectApproveRequest"];
export type ProjectRejectRequest =
  components["schemas"]["ProjectRejectRequest"];
/** 整单撤回(阶段三 F22):原因必填,空 → 422 NOTE_REQUIRED */
export type ProjectWithdrawRequest =
  components["schemas"]["ProjectWithdrawRequest"];
export type ProjectOutcomeRequest =
  components["schemas"]["ProjectOutcomeRequest"];
export type ProvisionalPriceRequest =
  components["schemas"]["ProvisionalPriceRequest"];
/** Transition endpoints return a dict; only these real service keys are consumed. */
export type ProjectTransitionResult = {
  review_status?: ProjectReview["state"];
  review?: ProjectReview;
  line_results?: Record<string, unknown>[];
};
export type ProjectTransitionAction =
  | "submit"
  | "approve"
  | "reject"
  | "reopen"
  | "withdraw";
export type ProjectTransitionBody =
  | ProjectApproveRequest
  | ProjectRejectRequest
  | ProjectWithdrawRequest;
export async function transitionQuoteProject(
  projectId: string,
  action: ProjectTransitionAction,
  body?: ProjectTransitionBody,
): Promise<ProjectTransitionResult> {
  const { data } = await apiClient.post<ProjectTransitionResult>(
    `/v1/quote-projects/${projectId}/${action}`,
    body,
    { _silentError: true },
  );
  return data;
}
export async function recordProjectOutcome(
  projectId: string,
  body: ProjectOutcomeRequest,
) {
  const { data } = await apiClient.post<
    components["schemas"]["ProjectOutcomeResponse"]
  >(`/v1/quote-projects/${projectId}/outcome`, body, { _silentError: true });
  return data;
}
export async function voidProjectOutcome(projectId: string) {
  const { data } = await apiClient.post<
    components["schemas"]["ProjectOutcomeResponse"]
  >(`/v1/quote-projects/${projectId}/outcome/void`, undefined, {
    _silentError: true,
  });
  return data;
}

/** 项目出新版本(阶段三 S3-3 · F22 · 设计 B4.4 · 决定 D3-14)的请求体:请求号必填(`sendWithRequestId` 带) */
export type ProjectNewVersionRequest =
  components["schemas"]["ProjectNewVersionRequest"];

/**
 * 已批准 / 部分批准的项目出新版本:201 → 新项目(和 `GET` 同形);同号重放 200 → 原来那个新项目。
 * 按复制出来的草稿张数扣额度(不透支,402 照现有额度提示说);已经有新版本 409 `PROJECT_ALREADY_SUPERSEDED`
 * (`successor_id`);项目正在被改 409 `PROJECT_BUSY`。错误由调用方就地说(静默)。
 */
export async function createQuoteProjectVersion(
  projectId: string,
  body: ProjectNewVersionRequest,
): Promise<QuoteProjectDto> {
  const { data } = await apiClient.post<QuoteProjectDto>(
    `/v1/quote-projects/${projectId}/new-version`,
    body,
    { _silentError: true, _errorContext: "项目出新版本失败" },
  );
  return data;
}

export type ProjectPrefillCancelResult =
  components["schemas"]["ProjectPrefillCancelOut"];

/**
 * 「取消全部」:项目里在途的 AI 识别全部取消(阶段三 S3-5)。逐行结果:`cancelled` 取消了、
 * `not_in_flight` 已经算完、`not_yours` 不是你发起的(没动)、`busy` 正在写回(稍后再试)。
 */
export async function cancelProjectPrefill(
  projectId: string,
): Promise<ProjectPrefillCancelResult> {
  const { data } = await apiClient.post<ProjectPrefillCancelResult>(
    `/v1/quote-projects/${projectId}/prefill:cancel`,
    undefined,
    { _silentError: true, _errorContext: "取消 AI 识别失败" },
  );
  return data;
}
